import ReactSelect, { components } from "react-select";
import { useFormContext } from "react-hook-form";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  channel_of_contact,
  CreatePartnerLeadType,
} from "./schema";
import { DateTimePicker } from "pages/Opportunities/dateandtime";
import { useEffect, useState, useMemo, useCallback } from "react";
import { AxiosRequestConfig } from "axios";
import {
  getAllStaff,
} from "service/apis/StaffDetailsapi";
import {
  getContactDetailById,
  getContactsPartnerList,
} from "service/apis/Opportunities";
import { useAppSelector } from "redux/hooks";
import { Option } from "pages/Opportunities/types";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { convertFileToBase64 } from "pages/CreateRemit/documentation";
import { useQuery } from "react-query";

const channelInteractionMap = {
  i_contacted: {
    meeting: 3,
    email: 2,
    phone: 1,
    in_person: 13,
  },
  partner_contacted: {
    phone: 5,
    email: 6,
    in_person: 7,
  },
};

// Memoized components to prevent unnecessary re-renders
const MultiValueLabel = ({ data, ...props }) => (
  <components.MultiValueLabel {...props}>
    <div className="flex items-center text-[#333333] rounded-full">
      {data.label}
    </div>
  </components.MultiValueLabel>
);

const MultiValueRemove = (props) => (
  <components.MultiValueRemove {...props}>
    <span className="cursor-pointer text-[#333333]">&times;</span>
  </components.MultiValueRemove>
);

// Memoized ReactSelectStyled component
const ReactSelectStyled = ({
  options,
  onChange,
  placeholder,
  isMulti = false,
  value,
}) => {
  const memoizedComponents = useMemo(() => ({
    MultiValueLabel,
    MultiValueRemove
  }), []);

  const memoizedStyles = useMemo(() => ({
    control: (provided, state) => ({
      ...provided,
      zIndex: 0,
      borderRadius: ".5rem",
      padding: ".3rem",
      borderColor: state.isFocused ? "#165FE3" : "#333333",
      boxShadow: "none",
      transition: "border-color 0.2s ease",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000,
    }),
  }), []);

  return (
    <ReactSelect
      options={options}
      onChange={onChange}
      value={value}
      isMulti={isMulti}
      placeholder={placeholder}
      className="w-full"
      components={memoizedComponents}
      menuPlacement="auto"
      styles={memoizedStyles}
      menuPosition='fixed'
    />
  );
};

export function isStep3Valid({
  contacted_type,
  channelofcontact_type,
  meeting_time,
  channel_of_interaction_id,
  meeting_members,
}) {
  if (!contacted_type) return false;
  if (!channelofcontact_type) return false;
  if (!channel_of_interaction_id) return false;

  if (channelofcontact_type === "meeting") {
    if (!meeting_time) return false;
    if (!meeting_members || meeting_members.length === 0) return false;
  }

  return true;
}

export default function Step3() {
  const [date, setDate] = useState<Date>();
  const location = useLocation();
  const methods = useFormContext<CreatePartnerLeadType>();
  const { loading, error, success } = useAppSelector(
    (state) => state.PartnerAcquisition,
  );

  // Watch form values
  const {
    partner_type,
    partner_subtype,
    name,
    contacted_type,
    channelofcontact_type,
    meeting_time,
    channel_of_interaction_id,
    meeting_members,
    visiting_card,
  } = methods.watch();

  // Get auth token once
  const authToken = useMemo(() => {
    const auth = localStorage.getItem("authToken");
    return auth ? JSON.parse(auth) : null;
  }, []);

  // API configuration
  const apiConfig: AxiosRequestConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${authToken}` },
  }), [authToken]);

  // React Query for staff data
  const { data: staffOptions = [] } = useQuery({
    queryKey: ['staff'],
    queryFn: async () => {
      const res = await getAllStaff(apiConfig);
      return res.data?.map((staff) => ({
        label: `${staff.fname}`,
        value: staff.id,
      })) || [];
    },
    enabled: !!authToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // React Query for contact partners
  const { data: contactPartnerOptions = [] } = useQuery({
    queryKey: ['contactPartners'],
    queryFn: async () => {
      const res = await getContactsPartnerList(apiConfig);
      return res.data?.map((contact) => ({
        label: contact.contactName,
        value: contact.id,
      })) || [];
    },
    enabled: !!authToken,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Get c_id from URL
  const cId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("c_id");
  }, [location.search]);

  // React Query for linked contact details
  const { data: linkedContactList = [] } = useQuery({
    queryKey: ['linkedContact', cId],
    queryFn: async () => {
      const payload = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { id: cId },
      };
      const response = await getContactDetailById(payload);
      if (response?.data) {
        return [{
          label: response.data.full_name,
          value: response.data.id,
        }];
      }
      return [];
    },
    enabled: !!authToken && !!cId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Set default values effect
  useEffect(() => {
    if (!contacted_type) {
      methods.setValue("contacted_type", "i_contacted", {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (!channelofcontact_type) {
      const defaultChannelId = channelInteractionMap["i_contacted"]["meeting"];
      methods.setValue("channelofcontact_type", "meeting", {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      methods.setValue("channel_of_interaction_id", defaultChannelId, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [contacted_type, channelofcontact_type, methods]);

  // Memoized handlers
  const handleChannelChange = useCallback((tab) => {
    if (channelofcontact_type === 'meeting') {
      methods.setValue("visiting_card", {
        filename: "",
        filetype: "",
        fileBase64: "",
      }, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    const channelId = channelInteractionMap[contacted_type]?.[tab] || 0;
    methods.setValue("channelofcontact_type", tab, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    methods.setValue("channel_of_interaction_id", channelId, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [channelofcontact_type, contacted_type, methods]);

  const handleContactTypeChange = useCallback((value) => {
    methods.setValue("contacted_type", value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [methods]);

  const handleTeamMembersChange = useCallback((selected: any) => {
    const selectedIds = selected.map((option: any) => option.value);
    methods.setValue("meeting_members", selectedIds, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [methods]);

  const handleContactedWithChange = useCallback((selected) => {
    methods.setValue("contacted_with", selected, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [methods]);

  const handleDateChange = useCallback((newDate) => {
    setDate(newDate);
    methods.setValue(
      "meeting_time",
      newDate?.toISOString() as string,
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      },
    );
  }, [methods]);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const base64 = await convertFileToBase64(file);
      methods.setValue("visiting_card.filename", file.name as string, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      methods.setValue("visiting_card.filetype", file.type as string, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      methods.setValue("visiting_card.fileBase64", base64 as string, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [methods]);

  // Memoized values for selects
  const selectedTeamMembers = useMemo(() => {
    return staffOptions.filter(option =>
      meeting_members?.includes(option.value)
    );
  }, [staffOptions, meeting_members]);

  const selectedContactedWith = useMemo(() => {
    return linkedContactList.find(option =>
      methods.getValues("contacted_with")?.value === option.value
    );
  }, [linkedContactList, methods.getValues("contacted_with")]);

  return (
    <div className="max-w-[500px]">
      <div className="flex flex-col sm:pl-0 pl-10 gap-8 w-full relative">
        {/* Contact Type Selection */}
        <div className="flex relative flex-col gap-4 w-full">
          <div
            className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${true ? "after:h-20 after:border-blue-700" : "after:h-0"
              }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
          >
            <img
              className="relative -left-10 "
              src="/images/overview/ellipse.svg"
              alt=""
            />
          </div>
          <RadioGroup
            className="flex w-full items-center gap-4"
            onValueChange={handleContactTypeChange}
            value={contacted_type}
          >
            <label
              htmlFor="i_contacted"
              className={`flex w-full items-center rounded-lg border p-4 space-x-3 transition-all duration-200 hover:shadow-sm cursor-pointer ${contacted_type === "i_contacted"
                  ? "border-blue-500 bg-blue-50 shadow-sm"
                  : "border-gray-300 hover:border-gray-400"
                }`}
            >
              <RadioGroupItem
                value="i_contacted"
                id="i_contacted"
                className="w-4 h-4"
              />
              <p className="font-medium">I contacted</p>
            </label>
            <label
              htmlFor="partner_contacted"
              className={`flex w-full items-center rounded-lg border p-4 space-x-3 transition-all duration-200 hover:shadow-sm cursor-pointer ${contacted_type === "partner_contacted"
                  ? "border-blue-500 bg-blue-50 shadow-sm"
                  : "border-gray-300 hover:border-gray-400"
                }`}
            >
              <RadioGroupItem
                value="partner_contacted"
                id="partner_contacted"
                className="w-4 h-4"
              />
              <p className="font-medium">Partner contacted</p>
            </label>
          </RadioGroup>
        </div>

        {/* Channel Selection */}
        {contacted_type && (
          <div className="flex relative flex-col gap-3 items-start justify-start w-full">
            <div
              className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${true ? "after:h-16 after:border-blue-700" : "after:h-0"
                }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
            >
              <img
                className="relative -left-10 "
                src="/images/overview/ellipse.svg"
                alt=""
              />
            </div>
            <Tabs
              value={channelofcontact_type}
              onValueChange={handleChannelChange}
            >
              <TabsList className="sm:flex-wrap gap-3 sm:mb-3 sm:justify-start">
                {channel_of_contact[contacted_type]?.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className={`py-1.5 px-[18px] h-[32px] rounded-lg ${tab.value === channelofcontact_type
                        ? "bg-navbar-menu-bg text-blue-700"
                        : "bg-[#F1F1F1]"
                      }`}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Meeting Details */}
        {contacted_type &&
          channelofcontact_type === "meeting" &&
          partner_subtype &&
          partner_type && (
            <div className="flex relative flex-col gap-6 items-start justify-start w-full">
              <div
                className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${true ? "after:h-0 after:border-blue-700" : "after:h-0"
                  }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
              >
                <img
                  className="relative -left-10 "
                  src="/images/overview/ellipse.svg"
                  alt=""
                />
              </div>

              <div className="w-full relative space-y-2">
                <div
                  className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${true ? "after:h-28 after:border-blue-700" : "after:h-0"
                    }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
                >
                  <img
                    className="relative -left-10 "
                    src="/images/overview/ellipse.svg"
                    alt=""
                  />
                </div>
                <label htmlFor="date" className="text-sm font-medium text-gray-700">
                  Select meeting time
                </label>
                <div className="">
                  <DateTimePicker
                    fromDate={new Date("2000-01-01")}
                    date={new Date(meeting_time)}
                    setDate={handleDateChange}
                  />
                </div>
              </div>

              <div className="w-full relative space-y-2">
                <div
                  className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${true ? "after:h-24 after:border-blue-700" : "after:h-0"
                    }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
                >
                  <img
                    className="relative -left-10 "
                    src="/images/overview/ellipse.svg"
                    alt=""
                  />
                </div>
                <label
                  htmlFor="meeting_members"
                  className="text-sm font-medium text-gray-700"
                >
                  Search & add my team members
                </label>
                <ReactSelectStyled
                  options={staffOptions}
                  onChange={handleTeamMembersChange}
                  value={selectedTeamMembers}
                  isMulti
                  placeholder="Select team members"
                />
              </div>

              <div className="w-full relative space-y-2">
                                <div
                  className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${true ? "after:h-28 after:border-blue-700" : "after:h-0"
                    }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
                >
                  <img
                    className="relative -left-10 "
                    src="/images/overview/ellipse.svg"
                    alt=""
                  />
                </div>
                <label
                  htmlFor="contacted_with"
                  className="text-sm font-medium text-gray-700"
                >
                  Contacted With
                </label>
                <ReactSelectStyled
                  options={linkedContactList}
                  onChange={handleContactedWithChange}
                  value={selectedContactedWith}
                  placeholder="Select contact"
                />
              </div>
            </div>
          )}

        {/* Notes Section */}
        <div className="w-full relative space-y-2">
                          <div
                  className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${channelofcontact_type === "meeting" ? "after:h-44 after:border-blue-700" : "after:h-0"
                    }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
                >
                  <img
                    className="relative -left-10 "
                    src="/images/overview/ellipse.svg"
                    alt=""
                  />
                </div>
          <div className="flex items-center gap-2 mb-2">
            <label htmlFor="notes" className="text-sm font-medium text-gray-700">
              Internal notes (optional)
            </label>
          </div>
          <textarea
            {...methods.register("notes")}
            name="notes"
            placeholder="Enter note"
            className={`border rounded-lg p-4 w-full min-h-[100px] transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${methods.formState.errors.root
                ? "border-red-500"
                : "border-gray-300 hover:border-gray-400"
              }`}
          />
        </div>

        {/* Visiting Card Upload */}
        {contacted_type &&
          channelofcontact_type === "meeting" &&
          partner_subtype &&
          partner_type && (
            <div className="w-full relative space-y-2">
                <div
                  className={`flex sm:hidden items-center top-0 flex-col after:right-10  ${false ? "after:h-44 after:border-blue-700" : "after:h-0"
                    }  absolute  after:relative  after:content-[''] after:border after:border-dashed `}
                >
                  <img
                    className="relative -left-10 "
                    src="/images/overview/ellipse.svg"
                    alt=""
                  />
                </div>
              <div className="flex items-center gap-2 mb-2">
                <label htmlFor="visiting_card_label" className="text-sm font-medium text-gray-700">
                  Visiting Card (Optional)
                </label>
              </div>
              <div className="flex items-center justify-between border rounded-lg h-12 py-2 px-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                <div className="flex items-center gap-3">
                  <svg width="17" height="22" viewBox="0 0 17 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.078 21.529H13.3007C15.2935 21.529 16.2849 20.5183 16.2849 18.516V9.77561C16.2849 8.53361 16.1502 7.99475 15.3801 7.20536L10.0663 1.80529C9.33523 1.05409 8.73805 0.890625 7.65048 0.890625L3.078 0.890625C1.09507 0.890625 0.09375 1.91084 0.09375 3.91348L0.09375 18.516C0.09375 20.5277 1.09507 21.529 3.078 21.529ZM3.1548 19.9794C2.16334 19.9794 1.64338 19.4496 1.64338 18.4873L1.64338 3.94223C1.64338 2.98938 2.16334 2.44025 3.16466 2.44025H7.43855V8.03336C7.43855 9.2462 8.05463 9.84297 9.24816 9.84297H14.7353V18.4873C14.7353 19.4496 14.2248 19.9794 13.2239 19.9794H3.1548ZM9.42148 8.38904C9.04609 8.38904 8.89166 8.23543 8.89166 7.85018V2.73884L14.4363 8.38945L9.42148 8.38904Z" fill="#5E5E5E" />
                  </svg>
                  <span className="text-gray-600 truncate max-w-[150px] inline-block text-sm">
                    {visiting_card?.filename || "Upload the documents"}
                  </span>
                </div>

                <label
                  htmlFor="visiting_card"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-blue-600 bg-white hover:bg-blue-50 h-9 px-4 py-2 text-blue-700 cursor-pointer"
                >
                  Upload
                </label>
                <Input
                  id="visiting_card"
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          )}
      </div>
    </div>
  );
}